'use client'

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { CheckCircle2, Loader2 } from 'lucide-react'

import { InviteByEmailField } from '@/components/employer/invite-by-email-field'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useProductInvite } from '@/hooks/use-product-invite'

const MIN_SENDING_MS = 2000

type InviteDialogView = 'form' | 'sending' | 'success'

type ProductInviteDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  headline: string
  description: string
  submitLabel?: string
}

export function ProductInviteDialog({
  open,
  onOpenChange,
  headline,
  description,
  submitLabel = 'Send invite',
}: ProductInviteDialogProps) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [successEmail, setSuccessEmail] = useState('')
  const [view, setView] = useState<InviteDialogView>('form')
  const inviteMutation = useProductInvite()
  const cancelledRef = useRef(false)

  useEffect(() => {
    if (open) {
      cancelledRef.current = false
      return
    }
    cancelledRef.current = true
    setEmail('')
    setError('')
    setSuccessEmail('')
    setView('form')
    inviteMutation.reset()
    // Reset local form state when the dialog closes; mutation identity is stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to open
  }, [open])

  useEffect(() => {
    if (!open || view !== 'form') return
    document.getElementById('product-invite-email')?.focus()
  }, [open, view])

  const handleSendAnother = () => {
    setSuccessEmail('')
    setError('')
    setView('form')
    inviteMutation.reset()
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError('')
    const trimmed = email.trim()
    if (!trimmed) {
      setError('Please enter an email address')
      return
    }
    cancelledRef.current = false
    setView('sending')
    const minWait = new Promise<void>((resolve) => {
      window.setTimeout(resolve, MIN_SENDING_MS)
    })
    const result = await inviteMutation
      .mutateAsync(trimmed)
      .then((data) => ({ ok: true as const, data }))
      .catch((err: unknown) => ({ ok: false as const, err }))
    await minWait
    if (cancelledRef.current) return
    if (result.ok) {
      setSuccessEmail(result.data.email)
      setEmail('')
      setView('success')
      return
    }
    const err = result.err
    setError(err instanceof Error ? err.message : 'Failed to send invitation')
    setView('form')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        {view === 'sending' ? (
          <>
            <DialogHeader>
              <DialogTitle>Sending invite…</DialogTitle>
              <DialogDescription className="sr-only">
                Please wait while we send the invite.
              </DialogDescription>
            </DialogHeader>
            <div
              className="flex justify-center py-6"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="size-8 animate-spin text-muted-foreground" aria-hidden />
            </div>
          </>
        ) : null}

        {view === 'success' ? (
          <>
            <DialogHeader role="status" aria-live="polite">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="size-5 shrink-0 text-success" aria-hidden />
                <DialogTitle>Invite sent</DialogTitle>
              </div>
              <DialogDescription>
                We sent an invite to {successEmail}.
              </DialogDescription>
            </DialogHeader>
            <Button type="button" size="sm" onClick={handleSendAnother}>
              Send another
            </Button>
          </>
        ) : null}

        {view === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>{headline}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>

            <InviteByEmailField
              value={email}
              onChange={setEmail}
              onSubmit={handleSubmit}
              isSubmitting={inviteMutation.isPending}
              placeholder="friend@email.com"
              submitLabel={submitLabel}
              submittingLabel="Sending…"
              inputId="product-invite-email"
            >
              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
            </InviteByEmailField>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
